import type { RouteLocationNormalizedGeneric } from 'vue-router'

import {
  DASHBOARD_ROUTE_NAMES as DASHBOARD_ROUTE_NAMES_09,
  isDashboardRoute as isDashboardRoute09,
  useDashboards,
} from '@/features/dashboards'

// The onlyDashboard landing seam (ledger B9): 07 owns the flag (`useRbac().onlyDashboard`),
// 09 owns `firstDashboardRoute` + `isDashboardRoute`, and 05's permissionGuard composes them.
// With ORP-18657 module 09's store is on `dev`, so this file stops being a stub with a
// home-route fallback and becomes the thin composition point the seam always described. Both
// signatures are unchanged — `permissionAccess.ts` did not move.
//
// ADOPTING 09'S LIST WIDENS RBAC, DELIBERATELY. The stub exempted three route names; 09's list
// adds a fourth, `AccountCenter`. Since `permissionAccess.ts:17` uses this predicate as the
// onlyDashboard redirect EXEMPTION, an onlyDashboard user may now stay on Account Center instead
// of being bounced to their first dashboard. Legacy (`router/index.js:87-92`) did not exempt it.
// This is not a drive-by: contract 09 §18 ratifies it — "Account Center ... stays inside
// dashboards ... `onlyDashboard` access includes Account Center intentionally" — and Account
// Center is the same grid, widget host and definitions store as any dashboard, so redirecting
// away from it was the anomaly.
//
// `GetStarted` stays 05's. Legacy exempted it from the same array, but it is an onboarding route,
// not a dashboard route, so it does not belong in module 09's list; it is exempted here instead.

/** 05's onboarding exemption, kept out of module 09's list on purpose (see above). */
const ONBOARDING_ROUTE_NAMES: readonly string[] = ['GetStarted']

/** Route names exempt from the onlyDashboard redirect: module 09's four + onboarding. */
export const DASHBOARD_ROUTE_NAMES: readonly string[] = [
  ...DASHBOARD_ROUTE_NAMES_09,
  ...ONBOARDING_ROUTE_NAMES,
]

/** A by-name route target; module 09's resolver fills `params` for a real dashboard. */
export interface FirstDashboardTarget {
  name: string
  params?: Record<string, string>
}

export function isDashboardRoute(to: Pick<RouteLocationNormalizedGeneric, 'name'>): boolean {
  return (
    isDashboardRoute09(to) ||
    (typeof to.name === 'string' && ONBOARDING_ROUTE_NAMES.includes(to.name))
  )
}

/**
 * Where a dashboard-only user lands (legacy `getValidFirstDashboard`, `router/index.js:91`).
 *
 * Module 09's resolver is pure, but it needs the definitions list, so it is reached through
 * `useDashboards()` — a Pinia read, safe here because the guard only calls this once the session
 * is `ready` (`permission.ts:30`). With no dashboards it returns `{name:'CustomDashboardCreate'}`,
 * so `permissionAccess.ts:19`'s self-redirect check still has a distinct name to compare and the
 * no-loop property of contract 05 §14 is unchanged.
 */
export function resolveFirstDashboard(): FirstDashboardTarget {
  return useDashboards().firstDashboardRoute.value
}
